import { Expose, Type } from 'class-transformer';
import { forkJoin, Observable, of, pipe, ReplaySubject, Subject, Subscription, throwError } from 'rxjs';
import { catchError, map, mergeMap, retry, tap, timeout } from 'rxjs/operators';

import { Packages, PackageType } from '../commons/commons';
import { Logger } from '../models/logger';
import { isEmpty } from './lodash-min';

export interface ErrorResponse {
    error: boolean;
    message: string;
}

export class NetworkError extends Error {
    static is(err: Error): boolean {
        return err instanceof NetworkError;
    }

    constructor(message) {
        super(message);
        this.name = 'NetworkError';

        Object.setPrototypeOf(this, NetworkError.prototype);
    }
}

export class ServiceError extends Error {
    status?: number;
    /**
     * Parsed 5xx response body, when the server sent one.
     *
     * Added 2026-09-05 (Mongo write-stall incident, MONGO-STALL-PLAN-v5.2.md §4,
     * correction 19). The schedule backpressure contract answers 503 with
     * `{code:'service_busy', retryAfterSeconds}` — a client that cannot see the
     * body cannot tell "the server asked me to wait" from "the server broke",
     * and today's client treats both as fatal and deletes the uploaded bytes.
     * 4xx already carried its body via ClientError; 5xx dropped it on the floor.
     */
    response?: any;
    /**
     * `Retry-After` in seconds, from the response header or the body's
     * `retryAfterSeconds`. See `LightHttp.readRetryAfterSeconds`. Undefined when
     * the server said nothing parseable — never 0-by-default, because "wait 0 s"
     * and "no instruction" must not be the same value to the retry policy.
     */
    retryAfterSeconds?: number;
    /**
     * The `Accept` header the failing request carried (whatever the client
     * code set explicitly), or `<none>` when no explicit Accept was set.
     * Captured inside LightHttp so client-side error reports can help
     * diagnose upstream `406 Could not parse Accept header` failures.
     */
    requestAcceptHeader?: string;

    static is(err: Error): boolean {
        return err instanceof ServiceError;
    }

    constructor(message, status?: number, requestAcceptHeader?: string) {
        super(message);
        this.name = 'ServiceError';
        this.status = status;
        this.requestAcceptHeader = requestAcceptHeader;

        Object.setPrototypeOf(this, ServiceError.prototype);
    }
}

export class ClientError extends Error {
    response?: any;
    status?: number;
    /**
     * `Retry-After` in seconds (header or body `retryAfterSeconds`), undefined
     * when absent. Set on the 409 `schedule_in_flight` answer, which the server
     * pairs with `Retry-After: 2` — the client used to treat a 409 as a generic
     * error and fire two blind retries at a claim that was still in flight
     * (2026-09-05 incident: 303 of 11,882 upload uuids got duplicate workflows).
     */
    retryAfterSeconds?: number;
    /**
     * R2-B3: the parsed `error="..."` token from the response's
     * `WWW-Authenticate` header (e.g. `invalid_token`, `step_up_required`),
     * captured inside LightHttp where the raw AjaxError.xhr is still in scope.
     * Used by the reactive refresh-and-replay backstop (Task 20) and step-up
     * handling (Task 20b). Undefined when no marker was present.
     */
    authError?: string;
    /**
     * The `Accept` header the failing request carried (whatever the client
     * code set explicitly), or `<none>` when no explicit Accept was set.
     * Captured inside LightHttp so client-side error reports can help
     * diagnose upstream `406 Could not parse Accept header` failures.
     */
    requestAcceptHeader?: string;

    static is(err: Error): boolean {
        return err instanceof ClientError;
    }

    constructor(message, response?: any, status?: number, authError?: string, requestAcceptHeader?: string) {
        super(message);
        this.name = 'ClientError';
        this.response = response;
        this.status = status;
        this.authError = authError;
        this.requestAcceptHeader = requestAcceptHeader;

        Object.setPrototypeOf(this, ClientError.prototype);
    }
}

export class UnknownHTTPError extends Error {
    /**
     * The HTTP status, when there was one. `0` for the transport-failure branch
     * (offline / blocked / CORS), which is exactly what the schedule retry
     * policy classifies as retryable. Added 2026-09-05 so the policy no longer
     * has to infer "network error" from the message string.
     */
    status?: number;
    /** `Retry-After` in seconds when the server sent one; see ServiceError. */
    retryAfterSeconds?: number;
    /**
     * The `Accept` header the failing request carried (whatever the client
     * code set explicitly), or `<none>` when no explicit Accept was set.
     */
    requestAcceptHeader?: string;

    static is(err: Error): boolean {
        return err instanceof UnknownHTTPError;
    }

    constructor(message, requestAcceptHeader?: string) {
        super(message);
        this.name = 'UnknownHTTPError';
        this.requestAcceptHeader = requestAcceptHeader;

        Object.setPrototypeOf(this, UnknownHTTPError.prototype);
    }
}

/**
 * Enum uses java peoperty naming convension since POJO to Bson converter require this convension
 */
export type MotiveLocationType = 'appPageTop' |
    'singlePageBaseTop' |
    'singlePageBaseLeft' |
    'singlePageBaseRight' |
    'fileUploadPageTop' |
    'fileUploadPageBottom' |
    'fileDownloadPageTop' |
    'fileDownloadPageWithInCurrentDownloads' |
    'fileDownloadPageWithInRecoveredDownloads' |
    'fileDownloadPageBottom' |
    'fileDownloadPageLeft' |
    'fileDownloadPageRight' |
    'homePageTop' |
    'memePageTop' |
    'convertPageTop' |
    'convertPageBelowFeatureTitle' |
    'convertPageInContent' |
    'convertPageLeft' |
    'convertPageRight' |
    'convertPageBottom' |
    'howToPageTop' |
    'pageAnchor';
export class MotiveLocationTypes {
    public static appPageTop: MotiveLocationType = 'appPageTop';
    public static singlePageBaseTop: MotiveLocationType = 'singlePageBaseTop';
    public static singlePageBaseLeft: MotiveLocationType = 'singlePageBaseLeft';
    public static singlePageBaseRight: MotiveLocationType = 'singlePageBaseRight';
    public static fileUploadPageTop: MotiveLocationType = 'fileUploadPageTop';
    public static fileUploadPageBottom: MotiveLocationType = 'fileUploadPageBottom';
    public static fileDownloadPageTop: MotiveLocationType = 'fileDownloadPageTop';
    public static fileDownloadPageWithInCurrentDownloads: MotiveLocationType = 'fileDownloadPageWithInCurrentDownloads';
    public static fileDownloadPageWithInRecoveredDownloads: MotiveLocationType = 'fileDownloadPageWithInRecoveredDownloads';
    public static fileDownloadPageBottom: MotiveLocationType = 'fileDownloadPageBottom';
    public static fileDownloadPageLeft: MotiveLocationType = 'fileDownloadPageLeft';
    public static fileDownloadPageRight: MotiveLocationType = 'fileDownloadPageRight';
    public static homePageTop: MotiveLocationType = 'homePageTop';
    public static memePageTop: MotiveLocationType = 'memePageTop';
    public static convertPageTop: MotiveLocationType = 'convertPageTop';
    public static convertPageBelowFeatureTitle: MotiveLocationType = 'convertPageBelowFeatureTitle';
    /**
     * Inside the SEO body, below the fold. Absent from the settings document on purpose, so it
     * resolves falsy and ships dark -- see docs/proposed/AD_PLACEMENT_DESIGN.md 8.
     */
    public static convertPageInContent: MotiveLocationType = 'convertPageInContent';
    public static convertPageLeft: MotiveLocationType = 'convertPageLeft';
    public static convertPageRight: MotiveLocationType = 'convertPageRight';
    public static convertPageBottom: MotiveLocationType = 'convertPageBottom';
    public static howToPageTop: MotiveLocationType = 'howToPageTop';
    /** Site-wide sticky, closable bottom anchor (our own manual unit; see MotiveInstant.kt pageAnchor). */
    public static pageAnchor: MotiveLocationType = 'pageAnchor';
}

export class MotiveSlotConfig {
    @Expose()
    appPageTop: boolean;
    @Expose()
    singlePageBaseTop: boolean;
    @Expose()
    singlePageBaseLeft: boolean;
    @Expose()
    singlePageBaseRight: boolean;
    @Expose()
    fileUploadPageTop: boolean;
    @Expose()
    fileUploadPageBottom: boolean;
    @Expose()
    fileDownloadPageTop: boolean;
    @Expose()
    fileDownloadPageWithInCurrentDownloads: boolean;
    @Expose()
    fileDownloadPageWithInRecoveredDownloads: boolean;
    @Expose()
    fileDownloadPageBottom: boolean;
    @Expose()
    fileDownloadPageLeft: boolean;
    @Expose()
    fileDownloadPageRight: boolean;
    @Expose()
    homePageTop: boolean;
    @Expose()
    memePageTop: boolean;
    @Expose()
    convertPageTop: boolean;
    @Expose()
    convertPageBelowFeatureTitle: string;
    /**
     * Not present in the settings document yet, so it resolves undefined -> falsy and the slot
     * ships dark. Switch it on server-side only after Auto Ads is scoped off these URLs;
     * running manual and Auto Ads on the same page compounds density unpredictably.
     * docs/proposed/AD_PLACEMENT_DESIGN.md 8.
     */
    @Expose()
    convertPageInContent: boolean;
    @Expose()
    convertPageLeft: boolean;
    @Expose()
    convertPageRight: boolean;
    @Expose()
    convertPageBottom: boolean;
    @Expose()
    howToPageTop: boolean;
    /** Site-wide sticky, closable bottom anchor -- our own manual unit (free=true, paid=false). */
    @Expose()
    pageAnchor: boolean;
}

export class MotiveConfig extends MotiveSlotConfig {

    /** @deprecated */
    front_page: boolean;
    /** @deprecated */
    convert_page: boolean;
    /** @deprecated */
    upload_box: boolean;
    /** @deprecated */
    download_page: boolean;
    internalMotivesEnabled: boolean;
    headerPromotionToBuy: boolean;
    show7DayTrialDialog: boolean;
    externalMotiveType: 'google' | 'buysell';
    showExtensionDownloadDialog: boolean;

    rotateMotives: false;
    motiveRotationPeriodInSeconds: number;
    motiveRotationRandonOffsetRangeInSeconds: number;

    bypassMouseEvents: MotiveLocationType[];

    constructor() {
        super();
    }
}

export class MotiveSettingsResponse {
    _free: MotiveConfig;
    free: MotiveConfig;
    paid: MotiveConfig;
    /** @deprecated */
    internalMotivesEnabled: boolean;
    /** @deprecated */
    headerPromotionToBuy: boolean;
    /** @deprecated */
    show7DayTrialDialog: boolean;
    /** @deprecated */
    externalMotiveType: 'google' | 'buysell';
    /** @deprecated */
    showExtensionDownloadDialog: boolean;
}

export class FromTypeFileSizeLimitInMB {
    powerpoint: number;
    excel: number;
    word: number;
    pdf: number;
    image: number;
    ps: number;
    video: number;
    audio: number;
    av: number;
    ebook: number;
    any: number;
}

export class UserLimitsSettings extends FromTypeFileSizeLimitInMB {
    numberOfConcurrentTasks: number;
}

/**
 * Site-wide feature flags. The wire format puts `featureFlags` at the top
 * level of the /v1/api/settings response (sibling to UserSettings fields).
 * Source of truth lives in `db.settings.featureFlags` on the backend.
 *
 * Two ways to subscribe (2026-09-02):
 *   - `settingsService.featureFlags` — bare flags, no provenance. Fine for a flag
 *     whose props-baked value is as trustworthy as the API's.
 *   - `settingsService.featureFlags$` — `{ flags, source }`, where `source` is
 *     `'props' | 'api'`. Required by any gate that must tell "the API said off" from
 *     "the build baked prod's flag set into the page props"; see `pdfEditor` below.
 */
export interface FeatureFlags {
    showLivePreview: boolean;
    useWebsockets: boolean;
    useTaskScheduling: boolean;
    bulkZip: boolean;
    showCodeStudioTabs: boolean;
    /**
     * Gates the portal subscription self-service surfaces:
     *   - `/portal/plans` page (tile picker)
     *   - `/portal/billing` cancellation alert + tile actions (T13)
     *   - "Plans" entry in the portal side-nav
     *
     * Default false. Backend default lives in
     * `springboot-shared-library/.../settings/FeatureFlags.kt` (T1).
     * Flipped to true in QA per qa-runbook 7.30 (Task 17).
     */
    portalPlansV2Enabled: boolean;
    /**
     * Portal v3 — gates the new IA (4-section sidebar, Overview dashboard,
     * new route layout). Mirror of backend FeatureFlags.portalV3Enabled.
     * Default false.
     */
    portalV3Enabled: boolean;
    refreshTokens: boolean;
    /**
     * Edit PDF (`/pdf-editor`) — gates the whole editor bundle. Mirror of backend
     * FeatureFlags.pdfEditor. Default false. The mount gate keys on the SOURCE of the
     * settings emission (see settings.service.ts `featureFlags$`), never on `undefined`:
     * xconvert-web-ssr bakes PROD's flag set into every build's props (2026-09-02), so a
     * props-sourced `false` is not evidence.
     */
    pdfEditor: boolean;
    /** Edit PDF v1.1 server unlock — gates the consent modal and the decrypt call only, never
     *  the editor. Default false; never true during the v1 soak. */
    pdfEditorServerUnlock: boolean;
    /**
     * Hands-off scheduling — gates the whole queued-card path on the schedule
     * call: keep the card and the uploaded bytes on 5xx/409/network, the
     * jittered retry ladder with `Retry-After`, the 4-way batch gate, the
     * `Idempotency-Key` header, the stalled watchdog and "Retry all".
     *
     * Default FALSE, and with it false the schedule path is byte-for-byte
     * today's behaviour (rxjs `retry(2, 1000)`, `removeFile` before the dialog).
     * Mirror of backend `FeatureFlags.clientQueuedState`.
     * MONGO-STALL-PLAN-v5.2.md §4 and §6 (rollout step 5, "client queued state,
     * web only"); the server halves (`idempotentSchedule`,
     * `scheduleBackpressure`) are independently killable flags of their own.
     */
    clientQueuedState: boolean;
}

export class FeatureFlags {
    showLivePreview: boolean;
    useWebsockets: boolean;
    useTaskScheduling: boolean;
    bulkZip: boolean;
    showCodeStudioTabs: boolean;
    portalPlansV2Enabled: boolean;
    portalV3Enabled: boolean;
    refreshTokens: boolean;
    pdfEditor: boolean;
    pdfEditorServerUnlock: boolean;
    /** See the interface above. Default false — flag off is today's behaviour. */
    clientQueuedState: boolean;
    /**
     * Send a browser-generated `X-Request-Id` on API/edge calls so a browser
     * error joins to the api and edge log lines that produced it
     * (METRICS.md §4.1). Default OFF — see the read site in MetricsService.
     */
    requestIdHeader: boolean;
}

export type PaymentStateType = 'unknown' |
    'signup' |
    'paid' |
    'cancelled';
export class PaymentStateTypes {
    static unknown = 'unknown';
    static signup = 'signup';
    static paid = 'paid';
    static cancelled = 'cancelled';
}

export type PaymentGatewayType = 'none' |
    'paypal' |
    'stripe';
export class PaymentGatewayTypes {
    static none = 'none';
    static paypal = 'paypal';
    static stripe = 'stripe';
}

export interface Rating {
    feature: string,
    rating_1: number,
    rating_2: number,
    rating_3: number,
    rating_4: number,
    rating_5: number,
}

/**
 * V2 entitlement summary, denormalized onto `users.entitlementsCache` by
 * `BillingEventApplier` (shared-lib). The FE reads it through
 * `AccountDetails.entitlementsCache` to make `isPaidUser()` correct during
 * the cancel-mid-period grace window — V1 fields (`paid`,
 * `subscriptionNextBillingDate`) can drift across gateways (PayPal flips
 * `paid=false` immediately on cancel even though access continues), but V2
 * state is the canonical source of truth.
 */
export interface EntitlementSummary {
    entitlementId?: string;
    type?: 'WEBSITE' | 'API';
    packageType?: string;
    state?: 'ACTIVE' | 'CANCELED_ACCESS_UNTIL_PERIOD_END' | 'PAST_DUE' | 'PAUSED' | 'EXPIRED';
    /** ISO date string from the server; we parse on read in `isPaidUser`. */
    activeUntil?: string | Date | null;
}

export class AccountDetails {
    /**
     * MongoDB ObjectId (hex string) for this user. Serialized by
     * springboot-shared-library's `ObjectIdConverter` (Gson TypeAdapter) →
     * 24-char hex, e.g. "6234abcd...". Used as `custom_id` on PayPal
     * subscription creates so the webhook handler can resolve a user by
     * this id when resolving by subscriptionId fails (cold start on a
     * brand-new sub; see paypal-sdk-upgrade.md §4.3.1 step 4c).
     */
    id?: string;
    active: boolean;
    @Type(() => Date)
    createdDate: Date;
    email: string;
    @Type(() => Date)
    lastUpdatedDate: Date;
    name?: string;
    packageType: PackageType;
    paymentDetails?: any;
    paymentGateway: PaymentGatewayType;
    paymentState: PaymentStateType; // TODO type these
    verified: boolean;
    paid: boolean;
    admin: boolean;
    @Type(() => Date)
    subscriptionNextBillingDate: Date;
    /**
     * V2 entitlement cache (denormalized from `user_entitlements` by
     * `BillingEventApplier`). Read by `isPaidUser()` as the canonical
     * source of truth — V1 cache fields are only the fallback for
     * legacy / pre-V2-backfill users.
     */
    entitlementsCache?: EntitlementSummary[];

    public isPaidUser() {
        const now = Date.now();
        // V2 entitlement (canonical) — handles CANCELED_ACCESS_UNTIL_PERIOD_END
        // correctly across all gateways, even when V1 `paid` flipped early
        // (PayPal cancel) or `subscriptionNextBillingDate` drifted.
        const v2Active = (this.entitlementsCache ?? []).some((e) => {
            if (e?.type !== 'WEBSITE') return false;
            if (e.state !== 'ACTIVE' && e.state !== 'CANCELED_ACCESS_UNTIL_PERIOD_END') return false;
            if (!e.activeUntil) return true;
            const until = e.activeUntil instanceof Date ? e.activeUntil : new Date(e.activeUntil);
            return until.getTime() > now;
        });
        if (v2Active) return true;

        // V1 fallback for legacy / pre-V2-backfill users.
        return (this.packageType === Packages.PACKAGE_TYPE_BASIC ||
            this.packageType === Packages.PACKAGE_TYPE_PRO) &&
            (
                this.paid ||
                ((this.subscriptionNextBillingDate && this.subscriptionNextBillingDate.getTime() > now - 1000 * 60 * 60 * 24 * 1)));
    }
}

export class UserSettings {
    useEdgeTTL: boolean;
    headerMessageEnabled: boolean;
    headerMessageEnabledForPaid: boolean;
    headerMessage: string;
    chunkUploadEnabled: boolean;
    chunkSize: number;
    simultaneousUploads: number;
    xhrTimeout: number;
    @Type(() => FromTypeFileSizeLimitInMB)
    highPriorityCountryFromTypeFileSizeLimitInMB: FromTypeFileSizeLimitInMB;
    @Type(() => FromTypeFileSizeLimitInMB)
    fromTypeFileSizeLimitInMB: FromTypeFileSizeLimitInMB;
    @Type(() => UserLimitsSettings)
    freeUserLimits: UserLimitsSettings;
    @Type(() => UserLimitsSettings)
    basicUserLimits: UserLimitsSettings;
    @Type(() => UserLimitsSettings)
    proUserLimits: UserLimitsSettings;
    // NOTE: featureFlags is NOT part of UserSettings as of 2026-05. It is
    // emitted by `SettingsService.featureFlags` as its own observable —
    // subscribe there for useWebsockets / useTaskScheduling / etc.
    @Type(() => MotiveSettingsResponse)
    motive: MotiveSettingsResponse;
    resolveMousemoveEventsStartup: boolean;
    featureDescriptionsDisabled: boolean;
    markdownDisabled: boolean;
}

export interface LoginResponse {
    error: boolean;
    bearer: string;
    registered: boolean;
    canGoOffline: boolean;
    motive: MotiveSettingsResponse;
    onlineWSDomains: string[];
}

export interface AuthenticateResponse {
    error: boolean;
    bearer: string;
    motive: MotiveSettingsResponse;
}

/** Only convert and rethorw know errors, else requestor should handle the error */
export const ResponsePipeForHttpInterceptor = pipe(
    catchError(response => {
        if (response instanceof ServiceError || response instanceof NetworkError) {
            throw response;
        }
        if (response.status === 0) {
            throw new NetworkError('Not connected to internet. Some services will not work.');
        } else if (response.error && response.error.error) {
            throw new ServiceError(response.error.message || response.error.errorMessage || 'Oops... Something went wrong.');
        }
        throw response;
    }),
    map((response: any) => {
        return response;
    }),
);


export const ResponsePipe = pipe(
    catchError(response => {
        if (response instanceof ServiceError || response instanceof NetworkError) {
            throw response;
        }
        if (response.status === 0) {
            throw new NetworkError('Not connected to internet. Some services will not work.');
        } else if (response.error && response.error.error) {
            throw new ServiceError(response.error.message || response.error.errorMessage || 'Oops... Something went wrong.');
        }
        console.error(response);
        if (response instanceof Error) {
            throw response;
        }
        throw new Error(`Ooops! There was error. Error: ${response.statusText}`);
    }),
    map((response: any) => {
        return response;
    }),
);

export class RequestUtils {
    private static get logger() {
        return Logger.getLogger('RequestUtils');
    };

    // Tested and working
    public static getSuccessValuesOrThrowSequential$<T>(observables$: Observable<T>[]): Observable<T> {
        const subject = new ReplaySubject<T>(1);
        const result$ = subject.asObservable();
        const shallowClonedObservables$ = observables$.map(a => a);
        const fn = (prevError) => {
            const observable = shallowClonedObservables$.pop();
            if (isEmpty(observable)) {
                subject.error(prevError);
                return;
            }
            try {
                observable.subscribe({
                    next: (result) => {
                        subject.next(result);
                        subject.complete();
                    },
                    error: (err) => {
                        fn(err);
                    }
                });
            } catch (e) {
                fn(e);
            }
        }
        setTimeout(() => fn(null), 0);
        return result$;
    }

    public static getSuccessValuesOrThrow$<T>(
        observables$: Observable<T>[],
        retryCount = 3,
        tryUntilMillies = 30000,
    ): Observable<T[]> {
        const subject = new Subject<T[]>();
        const results = [];
        const errors = [];
        const updateResults = () => {
            if (results.length + errors.length === observables$.length) {
                if (results.length > 0) {
                    subject.next(results);
                    subject.complete();
                } else {
                    if (errors.length > 0) {
                        subject.error(errors[0]);
                    } else {
                        subject.error(new Error('Unknown error'));
                    }
                }
            }
        }

        const subscriptions: Subscription[] = [];
        for (const observable$ of observables$) {
            const sub = observable$.pipe(
                tap(() => RequestUtils.logger.trace1("retry")),
                // timeout(tryUntilMillies),
                catchError(err => {
                    RequestUtils.logger.trace1("catchError")
                    return throwError(err);
                }),
                retry(retryCount),
            ).subscribe({
                next: (data) => {
                    results.push(data);
                    updateResults();
                },
                error: (err) => {
                    errors.push(err);
                    updateResults();
                }
            });
            subscriptions.push(sub);
        }

        // Clean up subscriptions when subject completes or errors
        subject.subscribe({
            complete: () => subscriptions.forEach(s => s.unsubscribe()),
            error: () => subscriptions.forEach(s => s.unsubscribe()),
        });

        return subject.asObservable();

        // merge(observables$.map(observable$ => {
        //     return observable$.pipe(
        //         tap(() => logger.trace1("retry")),
        //         timeout(tryUntilMillies),
        //         catchError(err => {
        //             logger.trace1("catchError")
        //             return throwError(err);
        //             // timer(tryUntilMillies).pipe(
        //             //     tap(() => logger.trace1("throwError")),
        //             //     mergeMap(() => throwError(err)),
        //             // );
        //         }),
        //         retry(retryCount),
        //     );
        // })).subscribe({
        //     next: (data) => {
        //         logger.trace1(data);
        //     },
        //     error: (err) => {
        //         logger.trace1(err);
        //     }
        // })
        // return combineLatest(
        //     observables$.map(observable$ => {
        //         return observable$.pipe(
        //             retry(retryCount),
        //             timeout(tryUntilMillies),
        //             catchError(err => {
        //                 return timer(tryUntilMillies).pipe(
        //                     mergeMap(() => throwError(err)),
        //                 );
        //             }),
        //         );
        //     })
        // ).pipe(
        //     map((result: (T | null)[]) => {
        //         const successfulResults = result.filter(res => !!res);
        //         if (successfulResults.length === 0) {
        //             throw new Error('getSuccessValuesOrThrow failed to result in at least one successful result');
        //         }
        //         return successfulResults;
        //     })
        // );
    }
    /**
     * Parallelly execute all the observables and return first succeeded value.  
     * @param observables$ 
     * @param tryUntilMillies 
     * @returns 
     */
    public static getSuccessValueOrThrow$<T>(
        observables$: Observable<T>[],
        // tryForEventMillies = 10000,
        tryUntilMillies = 30000,
    ): Observable<T> {
        return forkJoin(observables$.map(observable$ => observable$.pipe(
            timeout(tryUntilMillies),
            catchError(err => {
                // console.log(`HPY =========== getSuccessValueOrThrow 1`);
                return of({
                    error: true,
                    originalError: err,
                });
            })
        ))).pipe(
            mergeMap(([...result]) => {
                // console.log(`HPY =========== getSuccessValueOrThrow 2`);
                if (result.every(r => r['error'])) {
                    // console.log(`HPY =========== getSuccessValueOrThrow 3`);
                    // Re-throw the FIRST original error rather than collapsing to
                    // a generic `Error('All requests failed')`. The generic shape
                    // strips `status` + `response` fields and prevents downstream
                    // retry / catch logic (notably the EnforcementErrorService
                    // gate on processor-resolver retries) from recognising
                    // HTTP 429 `enforcement_error` / HTTP 402 `insufficient_credits`
                    // bodies — which manifests as a stuck "Upload Failed" badge
                    // instead of the user-facing enforcement modal.
                    const firstWithErr = (result as Array<{ originalError?: unknown }>).find(r => r && r.originalError);
                    if (firstWithErr && firstWithErr.originalError) {
                        throw firstWithErr.originalError;
                    }
                    throw new Error('All requests failed');
                }
                const _resultT: Observable<T> = of(result.find(r => !r['originalError']) as any);
                // console.log(`HPY =========== getSuccessValueOrThrow 4`);
                return _resultT;
            })
        );

        // return race(
        //     ...observables$.map(observable$ => {
        //         return observable$.pipe(
        //             timeout(tryUntilMillies),
        //             catchError(err => {
        //                 return timer(tryUntilMillies).pipe(
        //                     mergeMap(() => throwError(err)),
        //                 );
        //             }),
        //         );
        //     })
        // );
    }

    public static async getSuccessValueOrThrow<T>(...observables$: Observable<T>[]): Promise<T> {
        return new Promise((resolve, reject) => {
            this.getSuccessValueOrThrow$(observables$).subscribe(
                (data) => {
                    resolve(data);
                },
                (err) => {
                    reject(err);
                },
            );
        });
    }

    public static toErrorSuppressedObservable<T>(observable$: Observable<T>) {
        return observable$.pipe(
            catchError(err => {
                return of(null);
            })
        )
    }
}
